import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  MessageCircle, Plus, Trash2, Search, Users, Eye, EyeOff,
  Loader2, BarChart3, RefreshCw, ExternalLink, ArrowUpDown,
  Radar, Globe, FileText, ChevronDown, ChevronRight, Star,
  AlertCircle, CheckCircle, Clock, TrendingUp, Hash,
} from 'lucide-react';

import { fmtEST, fmtDateEST } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ─── Tab Components ─────────────────────────────────────────────────────────

function DashboardTab({ stats, onScan, scanning }) {
  if (!stats) return null;
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Mentions', value: stats.total_mentions, icon: MessageCircle, color: 'text-blue-600' },
          { label: 'Unreviewed', value: stats.unreviewed, icon: Eye, color: 'text-amber-600' },
          { label: 'Lead Signals', value: stats.total_leads, icon: TrendingUp, color: 'text-green-600' },
          { label: 'Competitors', value: stats.competitors_tracked, icon: Users, color: 'text-purple-600' },
          { label: 'Subreddits', value: stats.subreddits_monitored, icon: Hash, color: 'text-red-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-medium">{label}</p>
                  <p className="text-2xl font-bold">{value}</p>
                </div>
                <Icon className={`h-5 w-5 ${color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* API Status + Last Scan */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">API Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {stats.api_configured ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Reddit API configured (OAuth)</span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">Using public endpoints (rate limited). Set REDDIT_CLIENT_ID and REDDIT_CLIENT_SECRET for better results.</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Last Scan</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.last_scan ? (
              <div className="text-sm">
                <p><Clock className="h-3 w-3 inline mr-1" />{fmtEST(stats.last_scan.ran_at)}</p>
                <p className="text-slate-500">{stats.last_scan.new_mentions} new mentions found</p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">No scans yet. Run your first scan below.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Scan Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Run Reddit Scan</h3>
              <p className="text-sm text-slate-500">Search all configured subreddits for competitor mentions and lead signals</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => onScan('day')} disabled={scanning} variant="outline" size="sm">
                {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Last 24h
              </Button>
              <Button onClick={() => onScan('week')} disabled={scanning} size="sm">
                {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Last Week
              </Button>
              <Button onClick={() => onScan('month')} disabled={scanning} variant="outline" size="sm">
                {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Last Month
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MentionsTab() {
  const [mentions, setMentions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', competitor_id: '', subreddit: '', reviewed: '' });
  const [page, setPage] = useState(0);
  const [reviewDialog, setReviewDialog] = useState(null);

  const fetchMentions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * 50, limit: 50 });
      if (filters.search) params.set('search', filters.search);
      if (filters.competitor_id) params.set('competitor_id', filters.competitor_id);
      if (filters.subreddit) params.set('subreddit', filters.subreddit);
      if (filters.reviewed === 'true') params.set('reviewed', 'true');
      if (filters.reviewed === 'false') params.set('reviewed', 'false');

      const res = await axios.get(`${API}/api/social/reddit/mentions?${params}`, { withCredentials: true });
      setMentions(res.data.mentions);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load mentions');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchMentions(); }, [fetchMentions]);

  const handleReview = async (mention, sentiment, leadPotential) => {
    try {
      await axios.put(`${API}/api/social/reddit/mentions/${mention.id}/review`, {
        sentiment, lead_potential: leadPotential,
      }, { withCredentials: true });
      toast.success('Mention reviewed');
      setReviewDialog(null);
      fetchMentions();
    } catch {
      toast.error('Failed to review mention');
    }
  };

  const sentimentBadge = (s) => {
    if (!s) return null;
    const colors = { positive: 'bg-green-100 text-green-700', negative: 'bg-red-100 text-red-700', neutral: 'bg-slate-100 text-slate-700' };
    return <Badge className={colors[s] || colors.neutral}>{s}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <Input placeholder="Search mentions..." value={filters.search}
                onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(0); }} />
            </div>
            <div className="w-40">
              <Label className="text-xs">Subreddit</Label>
              <Input placeholder="e.g. jobs" value={filters.subreddit}
                onChange={e => { setFilters(f => ({ ...f, subreddit: e.target.value })); setPage(0); }} />
            </div>
            <div className="w-36">
              <Label className="text-xs">Status</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={filters.reviewed} onChange={e => { setFilters(f => ({ ...f, reviewed: e.target.value })); setPage(0); }}>
                <option value="">All</option>
                <option value="false">Unreviewed</option>
                <option value="true">Reviewed</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setFilters({ search: '', competitor_id: '', subreddit: '', reviewed: '' }); setPage(0); }}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : mentions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <MessageCircle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No mentions found</h3>
            <p className="text-sm text-slate-500">Run a scan to discover Reddit mentions</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-sm text-slate-500">{total} mention{total !== 1 ? 's' : ''} found</p>
          <div className="space-y-3">
            {mentions.map(m => (
              <Card key={m.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">r/{m.subreddit}</Badge>
                        {m.competitor_name && <Badge className="bg-purple-100 text-purple-700 text-xs">{m.competitor_name}</Badge>}
                        {m.is_lead_keyword && <Badge className="bg-green-100 text-green-700 text-xs">Lead Signal</Badge>}
                        {sentimentBadge(m.sentiment)}
                        {m.reviewed && <CheckCircle className="h-3 w-3 text-green-500" />}
                      </div>
                      <a href={m.url} target="_blank" rel="noopener noreferrer"
                        className="font-semibold text-sm mt-1 block hover:text-blue-600 truncate">
                        {m.title || '(no title)'}
                        <ExternalLink className="h-3 w-3 inline ml-1" />
                      </a>
                      {m.selftext && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{m.selftext.slice(0, 300)}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span>u/{m.author}</span>
                        <span>{m.score} pts</span>
                        <span>{m.num_comments} comments</span>
                        <span>Matched: "{m.keyword_matched}"</span>
                        {m.discovered_at && <span>{fmtDateEST(m.discovered_at)}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {!m.reviewed && (
                        <Button variant="outline" size="sm" onClick={() => setReviewDialog(m)}>
                          <Eye className="h-3 w-3 mr-1" /> Review
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="text-sm text-slate-500">Page {page + 1} of {Math.ceil(total / 50)}</span>
            <Button variant="outline" size="sm" disabled={(page + 1) * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Mention</DialogTitle>
            <DialogDescription>{reviewDialog?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sentiment</Label>
              <div className="flex gap-2 mt-1">
                {['positive', 'neutral', 'negative'].map(s => (
                  <Button key={s} size="sm" variant="outline"
                    onClick={() => handleReview(reviewDialog, s, reviewDialog?.lead_potential || 'low')}>
                    {s}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Lead Potential</Label>
              <div className="flex gap-2 mt-1">
                {['high', 'medium', 'low', 'none'].map(l => (
                  <Button key={l} size="sm" variant="outline"
                    onClick={() => handleReview(reviewDialog, reviewDialog?.sentiment || 'neutral', l)}>
                    {l}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CompetitorsTab() {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newComp, setNewComp] = useState({ name: '', keywords: '', category: 'direct', website: '', description: '' });
  const [seeding, setSeeding] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/social/reddit/competitors`, { withCredentials: true });
      setCompetitors(res.data);
    } catch {
      toast.error('Failed to load competitors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/api/social/reddit/competitors/seed`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetch();
    } catch {
      toast.error('Failed to seed competitors');
    } finally {
      setSeeding(false);
    }
  };

  const handleAdd = async () => {
    if (!newComp.name.trim()) { toast.error('Name is required'); return; }
    try {
      await axios.post(`${API}/api/social/reddit/competitors`, {
        ...newComp,
        keywords: newComp.keywords.split(',').map(k => k.trim()).filter(Boolean),
      }, { withCredentials: true });
      toast.success('Competitor added');
      setShowAdd(false);
      setNewComp({ name: '', keywords: '', category: 'direct', website: '', description: '' });
      fetch();
    } catch {
      toast.error('Failed to add competitor');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/social/reddit/competitors/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetch();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (comp) => {
    try {
      await axios.put(`${API}/api/social/reddit/competitors/${comp.id}`, {
        enabled: !comp.enabled,
      }, { withCredentials: true });
      fetch();
    } catch {
      toast.error('Failed to update');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{competitors.length} competitor{competitors.length !== 1 ? 's' : ''} tracked</p>
        <div className="flex gap-2">
          {competitors.length === 0 && (
            <Button onClick={handleSeed} disabled={seeding} variant="outline" size="sm">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Star className="h-4 w-4 mr-1" />}
              Seed Defaults
            </Button>
          )}
          <Button onClick={() => setShowAdd(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" /> Add Competitor
          </Button>
        </div>
      </div>

      {competitors.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No competitors configured</h3>
            <p className="text-sm text-slate-500 mb-4">Seed the default list or add competitors manually</p>
            <Button onClick={handleSeed} disabled={seeding}>
              {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Seed Default Competitors
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {competitors.map(c => (
            <Card key={c.id} className={!c.enabled ? 'opacity-50' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{c.name}</h3>
                      <Badge className={c.category === 'direct' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                        {c.category}
                      </Badge>
                    </div>
                    {c.description && <p className="text-xs text-slate-500 mt-1">{c.description}</p>}
                    {c.website && <p className="text-xs text-slate-400 mt-1">{c.website}</p>}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(c.keywords || []).map(k => (
                        <Badge key={k} variant="outline" className="text-xs">{k}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleToggle(c)} title={c.enabled ? 'Disable' : 'Enable'}>
                      {c.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Competitor</DialogTitle>
            <DialogDescription>Track a new competitor on Reddit</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={newComp.name} onChange={e => setNewComp(n => ({ ...n, name: e.target.value }))} placeholder="e.g. ConverzAI" />
            </div>
            <div>
              <Label>Keywords (comma-separated)</Label>
              <Input value={newComp.keywords} onChange={e => setNewComp(n => ({ ...n, keywords: e.target.value }))} placeholder="converzai, converz ai" />
            </div>
            <div>
              <Label>Category</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={newComp.category} onChange={e => setNewComp(n => ({ ...n, category: e.target.value }))}>
                <option value="direct">Direct Competitor</option>
                <option value="adjacent">Adjacent</option>
              </select>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={newComp.website} onChange={e => setNewComp(n => ({ ...n, website: e.target.value }))} placeholder="converzai.com" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newComp.description} onChange={e => setNewComp(n => ({ ...n, description: e.target.value }))} placeholder="Brief description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd}>Add Competitor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubredditsTab() {
  const [subreddits, setSubreddits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSub, setNewSub] = useState({ name: '', tier: 2, description: '' });
  const [seeding, setSeeding] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/social/reddit/subreddits`, { withCredentials: true });
      setSubreddits(res.data);
    } catch {
      toast.error('Failed to load subreddits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/api/social/reddit/subreddits/seed`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetch();
    } catch {
      toast.error('Failed to seed');
    } finally {
      setSeeding(false);
    }
  };

  const handleAdd = async () => {
    if (!newSub.name.trim()) { toast.error('Name is required'); return; }
    try {
      await axios.post(`${API}/api/social/reddit/subreddits`, newSub, { withCredentials: true });
      toast.success('Subreddit added');
      setShowAdd(false);
      setNewSub({ name: '', tier: 2, description: '' });
      fetch();
    } catch {
      toast.error('Failed to add');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/social/reddit/subreddits/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetch();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const tier1 = subreddits.filter(s => s.tier === 1);
  const tier2 = subreddits.filter(s => s.tier === 2);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{subreddits.length} subreddit{subreddits.length !== 1 ? 's' : ''} monitored</p>
        <div className="flex gap-2">
          {subreddits.length === 0 && (
            <Button onClick={handleSeed} disabled={seeding} variant="outline" size="sm">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Star className="h-4 w-4 mr-1" />}
              Seed Defaults
            </Button>
          )}
          <Button onClick={() => setShowAdd(true)} size="sm">
            <Plus className="h-4 w-4 mr-1" /> Add Subreddit
          </Button>
        </div>
      </div>

      {subreddits.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Hash className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No subreddits configured</h3>
            <p className="text-sm text-slate-500 mb-4">Seed the default list to get started</p>
            <Button onClick={handleSeed} disabled={seeding}>Seed Default Subreddits</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {[{ label: 'Tier 1 (Primary)', items: tier1 }, { label: 'Tier 2 (Secondary)', items: tier2 }].map(group => (
            group.items.length > 0 && (
              <div key={group.label}>
                <h3 className="text-sm font-semibold text-slate-600 mb-2">{group.label}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {group.items.map(s => (
                    <Card key={s.id}>
                      <CardContent className="p-3 flex items-center justify-between">
                        <div>
                          <a href={`https://reddit.com/r/${s.name}`} target="_blank" rel="noopener noreferrer"
                            className="font-medium text-sm hover:text-blue-600">
                            r/{s.name} <ExternalLink className="h-3 w-3 inline" />
                          </a>
                          {s.description && <p className="text-xs text-slate-400">{s.description}</p>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )
          ))}
        </>
      )}

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Subreddit</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Subreddit Name</Label>
              <Input value={newSub.name} onChange={e => setNewSub(n => ({ ...n, name: e.target.value }))} placeholder="e.g. recruiting" />
            </div>
            <div>
              <Label>Tier</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={newSub.tier} onChange={e => setNewSub(n => ({ ...n, tier: parseInt(e.target.value) }))}>
                <option value={1}>Tier 1 (Primary)</option>
                <option value={2}>Tier 2 (Secondary)</option>
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newSub.description} onChange={e => setNewSub(n => ({ ...n, description: e.target.value }))} placeholder="Why monitor this subreddit" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReportTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);

  const generateReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/social/reddit/report?days=${days}`, { withCredentials: true });
      setReport(res.data);
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div>
              <Label className="text-xs">Time Period</Label>
              <select className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={days} onChange={e => setDays(parseInt(e.target.value))}>
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            <Button onClick={generateReport} disabled={loading} className="mt-4">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
              Generate Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {report && (
        <>
          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Report Summary ({report.period_days} days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{report.summary.total_mentions}</p>
                  <p className="text-xs text-blue-600">Total Mentions</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{report.summary.total_leads}</p>
                  <p className="text-xs text-green-600">Lead Signals</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-700">{report.summary.competitors_tracked}</p>
                  <p className="text-xs text-purple-600">Competitors</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-700">{report.summary.subreddits_active}</p>
                  <p className="text-xs text-amber-600">Active Subreddits</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Competitor Breakdown */}
          {report.competitor_stats.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Competitor Mentions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium">Competitor</th>
                        <th className="text-right py-2 font-medium">Mentions</th>
                        <th className="text-right py-2 font-medium">Avg Score</th>
                        <th className="text-right py-2 font-medium">Comments</th>
                        <th className="text-left py-2 font-medium">Subreddits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.competitor_stats.map(c => (
                        <tr key={c._id} className="border-b">
                          <td className="py-2 font-medium">{c._id}</td>
                          <td className="py-2 text-right">{c.mention_count}</td>
                          <td className="py-2 text-right">{Math.round(c.avg_score)}</td>
                          <td className="py-2 text-right">{c.total_comments}</td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              {(c.subreddits || []).slice(0, 5).map(s => (
                                <Badge key={s} variant="outline" className="text-xs">r/{s}</Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Subreddit Breakdown */}
          {report.subreddit_stats.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Activity by Subreddit</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium">Subreddit</th>
                        <th className="text-right py-2 font-medium">Mentions</th>
                        <th className="text-right py-2 font-medium">Avg Score</th>
                        <th className="text-left py-2 font-medium">Competitors Mentioned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.subreddit_stats.map(s => (
                        <tr key={s._id} className="border-b">
                          <td className="py-2 font-medium">r/{s._id}</td>
                          <td className="py-2 text-right">{s.mention_count}</td>
                          <td className="py-2 text-right">{Math.round(s.avg_score)}</td>
                          <td className="py-2">
                            <div className="flex flex-wrap gap-1">
                              {(s.competitors_mentioned || []).filter(Boolean).slice(0, 5).map(c => (
                                <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Posts */}
          {report.top_posts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Posts by Engagement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {report.top_posts.slice(0, 10).map(p => (
                    <div key={p.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex-1 min-w-0">
                        <a href={p.url} target="_blank" rel="noopener noreferrer"
                          className="text-sm font-medium hover:text-blue-600 truncate block">
                          {p.title} <ExternalLink className="h-3 w-3 inline" />
                        </a>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span>r/{p.subreddit}</span>
                          <span>u/{p.author}</span>
                          {p.competitor_name && <Badge className="bg-purple-50 text-purple-600 text-xs">{p.competitor_name}</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 ml-4">
                        <span>{p.score} pts</span>
                        <span>{p.num_comments} comments</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lead Opportunities */}
          {report.lead_opportunities.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Lead Opportunities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {report.lead_opportunities.map(l => (
                    <div key={l.id} className="py-2 border-b last:border-0">
                      <a href={l.url} target="_blank" rel="noopener noreferrer"
                        className="text-sm font-medium hover:text-blue-600">
                        {l.title} <ExternalLink className="h-3 w-3 inline" />
                      </a>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        <span>r/{l.subreddit}</span>
                        <span>u/{l.author}</span>
                        <span>{l.score} pts</span>
                        <Badge className="bg-green-50 text-green-600 text-xs">"{l.keyword_matched}"</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: Radar },
  { key: 'mentions', label: 'Mentions', icon: MessageCircle },
  { key: 'competitors', label: 'Competitors', icon: Users },
  { key: 'subreddits', label: 'Subreddits', icon: Hash },
  { key: 'report', label: 'Report', icon: FileText },
];

export default function RedditIntelligencePage() {
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/social/reddit/stats`, { withCredentials: true });
      setStats(res.data);
    } catch {
      toast.error('Failed to load Reddit stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const handleScan = async (timeFilter) => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/api/social/reddit/scan`, { time_filter: timeFilter }, {
        withCredentials: true,
        timeout: 300000, // 5 min timeout for large scans
      });
      toast.success(`Scan complete: ${res.data.new_mentions} new mentions, ${res.data.lead_mentions} lead signals`);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Radar className="h-6 w-6 text-orange-600" />
            Reddit Intelligence
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Monitor competitor mentions, discover leads, and track industry conversations on Reddit
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {TABS.map(t => (
          <button key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-orange-600 text-orange-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'dashboard' && <DashboardTab stats={stats} onScan={handleScan} scanning={scanning} />}
      {tab === 'mentions' && <MentionsTab />}
      {tab === 'competitors' && <CompetitorsTab />}
      {tab === 'subreddits' && <SubredditsTab />}
      {tab === 'report' && <ReportTab />}
    </div>
  );
}
